import { ReactNode, useRef, useState } from "react";
import { UploadIcon } from "@radix-ui/react-icons";
import { ImageKind, ImagesService } from "client/openapi";
import { errorText } from "components/Forms/formsApi";

const MAX_SIDE = 512; // what is stored, on the long side
// Larger pictures are refused before resizing, to stay within the phone's
// memory. Phone photos (12 to 48 megapixels) fit.
const MAX_SOURCE_PIXELS = 50_000_000;
const MAX_BYTES = 1024 * 1024;

const toBlob = (canvas: HTMLCanvasElement, type: string) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.85));

/**
 * The picked picture, at most `maxSide` pixels on the long side, re-encoded
 * as the first of `types` the browser can write under 1 MB (else JPEG on
 * white). Re-encoding also drops the photo's location data.
 */
export async function shrink(
  file: File,
  maxSide = MAX_SIDE,
  types = ["image/webp"]
): Promise<Blob> {
  const source = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new Error("Use a PNG, JPEG, or WebP image."));
      image.src = source;
    });
    if (img.naturalWidth * img.naturalHeight > MAX_SOURCE_PIXELS)
      throw new Error("Use a picture under 50 megapixels.");
    const scale = Math.min(
      1,
      maxSide / Math.max(img.naturalWidth, img.naturalHeight)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const context = canvas.getContext("2d") as CanvasRenderingContext2D;
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const type of types) {
      const blob = await toBlob(canvas, type);
      if (blob && blob.type === type && blob.size <= MAX_BYTES) return blob;
    }
    context.fillStyle = "white";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await toBlob(canvas, "image/jpeg");
    if (!blob) throw new Error("That image couldn't be read.");
    if (blob.size > MAX_BYTES) throw new Error("Use an image under 1 MB.");
    return blob;
  } finally {
    URL.revokeObjectURL(source);
  }
}

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-semibold disabled:opacity-50";

/**
 * Upload, Replace and Remove for an org logo, an email logo or a tutor photo,
 * around the picture itself (`children`): a picture dropped anywhere on them
 * uploads as if it was chosen. The upload replaces that kind's image on the
 * server; Remove is the parent's request, which sends only the removal.
 * Photos never ride along with other saves.
 */
export default function ImageUpload({
  orgId,
  kind,
  tutorId,
  hasImage,
  noun,
  label,
  className = "",
  centered = false,
  children,
  onUploaded,
  onRemove,
}: {
  orgId: number;
  kind: ImageKind;
  tutorId?: number;
  hasImage: boolean;
  noun: "logo" | "photo";
  label?: ReactNode;
  className?: string;
  /** The buttons and the line under them centered (under a centered picture). */
  centered?: boolean;
  children?: ReactNode;
  onUploaded: (url: string) => void;
  onRemove: () => Promise<unknown>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [dragging, setDragging] = useState(false);

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError(undefined);
    try {
      const blob = await shrink(file);
      const { url } = await ImagesService.uploadImage({
        formData: { kind, org_id: orgId, tutor_id: tutorId, file: blob },
      });
      onUploaded(url);
    } catch (e) {
      // The server's reason, or why the picture couldn't be used.
      setError(
        errorText(e, e instanceof Error ? e.message : "That didn't upload.")
      );
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await onRemove();
    } catch (e) {
      setError(errorText(e, `The ${noun} wasn't removed. Try again.`));
    } finally {
      setBusy(false);
    }
  };

  // As the app's other drop areas: files only, and moving onto a child of
  // the area is still inside it. While an upload runs, the area takes no drop
  // (and says so with the cursor), but still handles it, so the browser never
  // opens the dropped file instead.
  const dragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    if (busy) e.dataTransfer.dropEffect = "none";
    else setDragging(true);
  };
  const dragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null))
      setDragging(false);
  };
  const drop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) upload(e.dataTransfer.files[0]);
  };

  return (
    <div
      data-drop-zone
      onDragOver={dragOver}
      onDragLeave={dragLeave}
      onDrop={drop}
      className={`relative isolate ${className}`}
    >
      {dragging && (
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-2 -z-10 rounded-xl border-2 border-dashed border-[#32cd32] bg-[#f0fff0]"
        />
      )}
      {children}
      <div className={centered ? "flex flex-col items-center text-center" : ""}>
        {label}
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <div
          className={`flex flex-wrap gap-2 ${centered ? "justify-center" : ""}`}
        >
          <button
            type="button"
            disabled={busy}
            onClick={() => input.current?.click()}
            className={`${BUTTON} border-green-600 text-green-700 hover:bg-green-50`}
          >
            <UploadIcon />
            {busy
              ? "Uploading…"
              : hasImage
              ? `Replace ${noun}`
              : `Upload ${noun}`}
          </button>
          {hasImage && (
            <button
              type="button"
              disabled={busy}
              onClick={remove}
              className={`${BUTTON} border-red-400 text-red-600 hover:bg-red-50`}
            >
              Remove
            </button>
          )}
        </div>
        {/* While a file is dragged over, the hint covers the line under the
          buttons instead of replacing it, so nothing around it moves. */}
        <p
          role={error ? "alert" : undefined}
          className={`relative mt-1.5 text-xs ${
            error ? "text-red-600" : "text-gray-400"
          }`}
        >
          <span className={dragging ? "invisible" : undefined}>
            {error ??
              `Drop or upload a PNG, JPG, or WebP.${
                noun === "logo" ? " Square works best." : ""
              }`}
          </span>
          {dragging && (
            <span className="absolute inset-0 font-semibold text-green-700">
              Drop the {noun} here.
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
